<script>
    import { onDestroy, onMount } from "svelte";

    import { isMobile, isUserStayOnTab } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SearchSelect,
        SvelteTable,
        PageLoading,
        Button,
        PostCsvFileModal,
        RecordPaymentModal,
        TableElementEditableReceiveDate,
        ResetFilterLink,
        DefaultDoctorFilter,
        CollapsibleTableIcons,
        TableElementEditableStatus,
        DoctorMultiSearchSelect,
        TableElementEditableTransactionDate,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { user } from "../../stores";

    import {
        goToURL,
        replaceURL,
        getEstimatePaidBy,
        getEstimatePaidMethod,
        getText,
        getFormatDateAndTime,
        getAmountInCurrency,
        splitPaymentAmount,
        getTitle,
        showError,
        getEstimatePaidFor,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getPaymentStatus,
        useTableScrollMemory,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";
    import {
        startRefreshTable,
        stopRefreshTable,
    } from "../../Utils/utilCache.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == 100;

    $: isUserEnableSeeDoctorFilter = $user.role >= 20;

    $: isUserEnableSeePaymentStatusColumn = $user.role >= 20;

    $: if (!$isUserStayOnTab) stopRefreshTable(refreshTableInterval);
    else
        refreshTableInterval = startRefreshTable(
            {
                tableIntervalRef: refreshTableInterval,
            },
            () => reloadReceipt(),
        );

    let isGettingReceipts = false,
        returnReceiptsArr = [],
        filters = { paidBy: "" },
        selectedReceipt,
        page = 0,
        pageSize = 25,
        count,
        isPostCSVModalOpen = false,
        isRecordPaymentModalOpen = false,
        isEditPaymentModalOpen = false,
        selectedPaymentDetails = {},
        showFilters = false;

    let selectedDoctors = [];

    let refreshTableInterval, scrollTableContainerRef;

    const { saveScroll, restoreScrollPosition } =
        useTableScrollMemory("PendingRefund");

    $: columns = [
        {
            key: "id",
            title: "Receipt ID",
            headerClass:
                "text-left px-3 sortable-header-decoration rounded-tl-xl",
            class: "text-left px-3",
            renderValue: (v) => _getReceiptId(v.id),
        },
        {
            key: "paymentId",
            title: "Payment ID",
            headerClass: "text-left px-3 ",
            class: "text-left px-3",
            renderValue: (v) => _getPaymentId(v),
        },
        {
            key: "receiptType",
            title: "For",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) =>
                _getReceiptType(
                    getEstimatePaidFor(v.relTo),
                    v.relId,
                    v.relNumber,
                ),
            csvContent: (v) =>
                _getReceiptType(
                    getEstimatePaidFor(v.relTo),
                    v.relId,
                    v.relNumber,
                    true,
                ),
        },
        {
            key: "encounterID",
            title: "Enc. ID",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 underline cursor-pointer",
            renderValue: (v) => getText(v.patientEncounterId),
        },
        {
            key: "paidDate",
            title: "Date Created",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3",
            renderValue: (v) => _getPaymentDate(v.paidDate),
        },
        {
            key: "transactionDate",
            title: "Date Received",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderComponent: {
                component: TableElementEditableTransactionDate,
                props: {
                    type: "ReceiptsTable",
                    onFinish: () =>
                        loadReceipts(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ),
                },
            },
            csvContent: (v) => {
                const { payments } = v;

                if (
                    !payments.length ||
                    !payments[0].transactionDate ||
                    payments[0].transactionDate == "0001-01-01T00:00:00"
                )
                    return "-";

                return payments[0].transactionDate;
            },
        },
        {
            key: "receiveDate",
            title: "Date Reconciled",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderComponent: {
                component: TableElementEditableReceiveDate,
                props: {
                    type: "ReceiptsTable",
                    onFinish: () =>
                        loadReceipts(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ),
                },
            },
            csvContent: (v) => {
                const { payments } = v;

                if (
                    !payments.length ||
                    !payments[0].receivedDate ||
                    payments[0].receivedDate == "0001-01-01T00:00:00"
                )
                    return "-";

                return payments[0].receivedDate;
            },
        },
        {
            key: "paymentAmount",
            title: "Subtotal Amount",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            value: (v) => _getSubtotalAmount(v.payments),
            csvContent: (v) => _getSubtotalAmount(v.payments, true),
        },
        {
            key: "processingFee",
            title: "Processing Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => _getProcessingFee(v.payments),
            csvContent: (v) => _getProcessingFee(v.payments, true),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) => _getGST(v.payments),
            csvContent: (v) => _getGST(v.payments, true),
        },
        {
            key: "total",
            title: "Total Amount",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) => _getTotal(v.payments),
            csvContent: (v) => _getTotal(v.payments, true),
        },
        {
            key: "paidBy",
            title: "Paid by",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) => _getPaidBy(v.payments),
            csvContent: (v) => _getPaidBy(v.payments, true),
        },
        {
            key: "paymentMethod",
            title: "Payment method",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) => _getPaymentMethod(v.payments),
            csvContent: (v) => _getPaymentMethod(v.payments, true),
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) => _renderDoctor(v.doctorName),
            csvContent: (v) => _renderDoctor(v.doctorName, true),
        },
        {
            key: "patient",
            title: "Patient",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) => _renderPatient(v.patientName),
            csvContent: (v) => _renderPatient(v.patientName, true),
        },
        {
            key: "transactionId",
            title: "Transaction ID",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderValue: (v) => _getTransactionID(v.payments),
            csvContent: (v) => _getTransactionID(v.payments, true),
        },
        {
            key: "paymentStatus",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            isHidden: !isUserEnableSeePaymentStatusColumn,
            renderComponent: {
                component: TableElementEditableStatus,
                props: {
                    type: "payment",
                    onFinish: () =>
                        loadReceipts(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ),
                },
            },
            csvContent: (v) => {
                const { payments } = v;

                if (!payments || !payments.length) return "-";

                return getPaymentStatus(payments[0].status);
            },
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: CollapsibleTableIcons,
                props: {
                    icons: ["editReceiptIcon"],
                    tableType: "Receipt",
                },
            },
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "paidDate"];

    onMount(async () => {
        if (params.query) await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        loadReceipts(page * pageSize, pageSize, _getSearchParams());
    });

    onDestroy(() => {
        stopRefreshTable(refreshTableInterval);
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (
                    arr[i].includes("doctorId=") &&
                    isUserEnableSeeDoctorFilter
                ) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("patientEncounter="))
                    filters.patientEncounterId = arr[i]
                        .split("=")[1]
                        .toString();

                if (arr[i].includes("fromDate="))
                    filters.dateFrom = arr[i].split("=")[1];

                if (arr[i].includes("toDate="))
                    filters.dateTo = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.searchInput = arr[i].split("=")[1];

                if (arr[i].includes("paidBy="))
                    filters.paidBy = arr[i].split("=")[1];
            }
            updateURL();
        }
    };

    const loadReceipts = async (start, limit, params) => {
        isGettingReceipts = true;
        if (params.sort) params.sort = params.sort.toLowerCase();
        if (params["filter[patientEncounterId]"]) {
            params = {
                "filter[patientEncounterId]":
                    params["filter[patientEncounterId]"],
            };
        }

        await apiMethods.getMethod
            .getReceipts(start, limit, params)
            .then((res) => _handleReceipts(res))
            .then(() => _updateHeaderSortIcon());

        isGettingReceipts = false;
    };

    const reloadReceipt = async () => {
        saveTableScroll();
        await apiMethods.getMethod
            .getReceipts(page * pageSize, pageSize, _getSearchParams())
            .then((res) => _handleReceipts(res, false, true))
            .then(() => restoreTableScroll())
            .then(() => _updateHeaderSortIcon());
    };

    const saveTableScroll = () => {
        if (scrollTableContainerRef)
            saveScroll(
                scrollTableContainerRef.scrollTop,
                scrollTableContainerRef.scrollLeft,
            );
    };

    const restoreTableScroll = () => {
        if (!scrollTableContainerRef) return;

        scrollTableContainerRef.scrollTop = restoreScrollPosition().top;
        scrollTableContainerRef.scrollLeft = restoreScrollPosition().left;

        saveScroll();
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadReceipts(0, pageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className, id } }, rowData, key) => {
        if (key == "actions" && className.includes("editReceiptIcon")) {
            selectedPaymentDetails = { ...rowData.payments[0] };
            openEditPaymentModal();
        } else if (key == "receiptType" && rowData.relId) {
            goToURL(`/${rowData.relToName}s/view/${rowData.relId}`);
        } else if (key == "doctor" && rowData.doctorId)
            goToURL(`/Account/view/${rowData.doctorId}`);
        else if (key == "patient" && rowData.patientId)
            goToURL(`/Patient/view/${rowData.patientId}`);
        else if (key == "encounterID" && rowData.patientEncounterId)
            goToURL(
                `/PatientEncounter/view/${rowData.patientEncounterId}/tab=billing`,
            );
        else __resetFocus__();
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadReceipts(0, pageSize, _getSearchParams());
        }
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Receipt ID") ||
                        (params.sort == "paidDate" &&
                            row.childNodes[0].data == "Date Created")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Receipt ID") ||
                        (params.sort == "paidDateDesc" &&
                            row.childNodes[0].data == "Date Created")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadReceipts(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadReceipts(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/PendingRefund/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const openPostCSVModal = () => (isPostCSVModalOpen = true);

    const closePostCSVModal = () => (isPostCSVModalOpen = false);

    const openRecordPaymentModal = () => (isRecordPaymentModalOpen = true);

    const closeRecordPaymentModal = (needReload = false) => {
        isRecordPaymentModalOpen = false;

        needReload &&
            loadReceipts(page * pageSize, pageSize, _getSearchParams());
    };

    const openEditPaymentModal = () => (isEditPaymentModalOpen = true);

    const closeEditPaymentModal = (needReload = false) => {
        isEditPaymentModalOpen = false;
        selectedPaymentDetails = {};

        needReload &&
            loadReceipts(page * pageSize, pageSize, _getSearchParams());
    };

    // HELPER FUNCTIONS
    const _handleReceipts = async (
        res,
        isCsvContent = false,
        isReload = false,
    ) => {
        if (res.error) showError(res.error);
        else {
            let results = isReload ? returnReceiptsArr : [];

            results = [...res.results];

            if (!isCsvContent) {
                returnReceiptsArr = [...res.results];
                count = res.total;
            }

            if (isCsvContent) return results;
            else returnReceiptsArr = [...results];
        }
    };

    const _getSearchParams = () => {
        let params = { "filter[paymentstatus]": 4 };

        if (filters.sort) params.sort = filters.sort;
        if (filters.doctorId && isUserEnableSeeDoctorFilter)
            params["filter[doctorId]"] = filters.doctorId;

        if (filters.patientEncounterId)
            params["filter[patientEncounterId]"] = filters.patientEncounterId;

        if (filters.paidBy || filters.paidBy == 0)
            params["filter[paidBy]"] = filters.paidBy;

        if (filters.dateFrom)
            params["filter[createdDateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[createdDateTo]"] = filters.dateTo;

        if (filters.searchInput) params.search = filters.searchInput;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params["filter[doctorId]"] && isUserEnableSeeDoctorFilter)
                str += `&doctorId=${params["filter[doctorId]"]}`;

            if (params["filter[patientEncounterId]"])
                str += `&patientEncounter=${params["filter[patientEncounterId]"]}`;

            if (params["filter[paidBy]"] || params["filter[paidBy]"] == 0)
                str += `&paidBy=${params["filter[paidBy]"]}`;

            if (params["filter[createdDateFrom]"])
                str += `&fromDate=${params["filter[createdDateFrom]"]}`;

            if (params["filter[createdDateTo]"])
                str += `&toDate=${params["filter[createdDateTo]"]}`;

            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _getReceiptId = (receiptId) => `${receiptId}`;

    const _getPaymentId = ({ payments }) => {
        if (!payments || !payments.length) return "";

        let res = [];

        payments.map((p) => res.push(p.id));

        return res.join(",");
    };

    const _getReceiptType = (type, relId, relNumber, isCsvContent = false) => {
        return isCsvContent
            ? `${type} ${relId} (${relNumber})`
            : `<div class="cursor-pointer hover:underline">${type} ${relId} (${relNumber})</div>`;
    };

    const _getPaymentDate = (date) => {
        if (date) {
            const dateArr = date.split("T")[0].split("-");
            if (Number(dateArr[0]) < 1900) return "-";
            return getFormatDateAndTime(date);
        } else return "-";
    };

    const _renderDoctor = (doctorName, isCsvContent = false) =>
        isCsvContent
            ? `${doctorName ? doctorName : "-"}`
            : `<div class="cursor-pointer hover:underline">
                    ${doctorName ? doctorName : "-"}
                </div>`;

    const _renderPatient = (patientName, isCsvContent = false) =>
        isCsvContent
            ? `${patientName ? patientName : "-"}`
            : `<div class="cursor-pointer hover:underline">
                    ${patientName ? patientName : "-"}
                </div>`;

    const _getSubtotalAmount = (paymentsArr = [], isCsvContent = false) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                isCsvContent
                    ? `${getAmountInCurrency(
                          splitPaymentAmount(payment).amount,
                      )}`
                    : `<div>${getAmountInCurrency(
                          splitPaymentAmount(payment).amount,
                      )}</div>`,
            );
        });

        return result.join(isCsvContent ? "\r\n" : "<br>").toString();
    };

    const _getProcessingFee = (paymentsArr, isCsvContent = false) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                isCsvContent
                    ? `${getAmountInCurrency(
                          splitPaymentAmount(payment).processing,
                      )}`
                    : `<div>${getAmountInCurrency(
                          splitPaymentAmount(payment).processing,
                      )}</div>`,
            );
        });

        return result.join(isCsvContent ? "\r\n" : "<br>").toString();
    };

    const _getGST = (paymentsArr, isCsvContent = false) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                isCsvContent
                    ? `${getAmountInCurrency(
                          payment.includesGST
                              ? splitPaymentAmount(payment).gst
                              : 0,
                      )}`
                    : `<div>${getAmountInCurrency(
                          payment.includesGST
                              ? splitPaymentAmount(payment).gst
                              : 0,
                      )}</div>`,
            );
        });

        return result.join(isCsvContent ? "\r\n" : "<br>").toString();
    };

    const _getTotal = (paymentsArr, isCsvContent = false) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                isCsvContent
                    ? `${getAmountInCurrency(
                          splitPaymentAmount(payment).total,
                      )}`
                    : `<div>${getAmountInCurrency(
                          splitPaymentAmount(payment).total,
                      )}</div>`,
            );
        });

        return result.join(isCsvContent ? "\r\n" : "<br>").toString();
    };

    const _getPaidBy = (paymentsArr, isCsvContent = false) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                isCsvContent
                    ? `${getEstimatePaidBy(payment.paidBy)}`
                    : `<div>${getEstimatePaidBy(payment.paidBy)}</div>`,
            );
        });

        return result
            .toString()
            .replaceAll(",", isCsvContent ? "\r\n" : "<br>");
    };

    const _getPaymentMethod = (paymentsArr, isCsvContent = false) => {
        if (!paymentsArr.length) return "-";

        const result = [];

        paymentsArr.forEach((payment) => {
            result.push(
                isCsvContent
                    ? `${getEstimatePaidMethod(payment.paymentMethod)}`
                    : `<div>${getEstimatePaidMethod(payment.paymentMethod)}</div>`,
            );
        });

        return result
            .toString()
            .replaceAll(",", isCsvContent ? "\r\n" : "<br>");
    };

    const _getTransactionID = (paymentsArr, isCsvContent = false) => {
        if (!paymentsArr.length) return "-";
        const result = [];
        paymentsArr.forEach((payment) => {
            result.push(
                isCsvContent
                    ? `${getText(payment.transactionId)}`
                    : `<div>${getText(payment.transactionId)}</div>`,
            );
        });
        return result
            .toString()
            .replaceAll(",", isCsvContent ? "\r\n" : "<br>");
    };

    const __resetFocus__ = () => {
        returnReceiptsArr.map((receipt) => (receipt.focused = false));

        selectedReceipt = {};

        returnReceiptsArr = [...returnReceiptsArr];
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
            on:click={toggleFilters}
        >
            Filters
            {#if isUserAdmin}
                <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
            {/if}
            <i
                style="font-size:14px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>

        {#if showFilters}
            <div
                class="flex flex-0 items-start flex-col-reverse gap-y-4"
                in:filterTransition={{ duration: 200, y: -100 }}
            >
                <div class="flex flex-1 gap-4 items-end flex-wrap">
                    <SearchInput
                        bind:value={filters.searchInput}
                        title="Search, Receipt Id or Payment Id"
                        inputFunc={handleFilterSelect}
                        isLong
                    />

                    {#if isUserEnableSeeDoctorFilter}
                        <DefaultDoctorFilter
                            bind:filter={filters}
                            filterPropertyName={"doctorId"}
                            onChangeFunc={() => {
                                selectedDoctors =
                                    filters["doctorId"].split(",");
                                handleFilterSelect();
                            }}
                        />

                        <DoctorMultiSearchSelect
                            bind:searchResult={selectedDoctors}
                            onChangeFunc={handleSelectDoctor}
                            inputClass="w-32"
                            zIndex="3"
                        />
                    {/if}

                    <SearchInput
                        bind:value={filters.patientEncounterId}
                        title="Patient Encounter ID"
                        type="number"
                        inputFunc={handleFilterSelect}
                    />

                    <SearchSelect
                        bind:value={filters.paidBy}
                        title="Paid By"
                        onChangeFunc={handleFilterSelect}
                    >
                        <option value="">All</option>
                        <option value="0">Patient</option>
                        <option value="1,2">Health Fund</option>
                    </SearchSelect>

                    {#if !$user.settings?.disableDateRangeInput}
                        <SearchDateRangeInput
                            bind:startDate={filters.dateFrom}
                            bind:endDate={filters.dateTo}
                            title="Date Range"
                            inputClass="borderB w-[272px]"
                            on:change={handleFilterSelect}
                            showClearButton
                            disable={isGettingReceipts}
                            showScrollYearOptions
                        />
                    {:else}
                        <SearchInput
                            bind:value={filters.dateFrom}
                            title="Date from"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingReceipts}
                            maxDate={filters.dateTo ?? undefined}
                            showScrollYearOptions
                        />

                        <SearchInput
                            bind:value={filters.dateTo}
                            title="Date to"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingReceipts}
                            minDate={filters.dateFrom ?? undefined}
                            showScrollYearOptions
                        />
                    {/if}

                    <ResetFilterLink
                        bind:filter={filters}
                        defaultFilter={{ paidBy: "" }}
                        onResetFilter={clearFilterDoctor}
                    />
                </div>
                {#if isUserAdmin}
                    <div class="flex flex-row flex-wrap gap-4">
                        <Button clickFunc={openPostCSVModal}>
                            Upload EFT Payments CSV
                        </Button>

                        <Button clickFunc={openRecordPaymentModal}>
                            Record Payment
                        </Button>
                    </div>
                {/if}
            </div>
        {/if}
    {:else}
        <div
            class="flex flex-0 items-start justify-between"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <div class="flex flex-1 gap-4 items-end flex-wrap">
                <SearchInput
                    bind:value={filters.searchInput}
                    title="Search, Receipt Id or Payment Id"
                    inputFunc={handleFilterSelect}
                    isLong
                />

                {#if isUserEnableSeeDoctorFilter}
                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="3"
                    />
                {/if}

                <SearchInput
                    bind:value={filters.patientEncounterId}
                    title="Patient Encounter ID"
                    type="number"
                    inputFunc={handleFilterSelect}
                />

                <SearchSelect
                    bind:value={filters.paidBy}
                    title="Paid By"
                    onChangeFunc={handleFilterSelect}
                >
                    <option value="">All</option>
                    <option value="0">Patient</option>
                    <option value="1,2">Health Fund</option>
                </SearchSelect>

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.dateFrom}
                        bind:endDate={filters.dateTo}
                        title="Date Range"
                        inputClass="borderB w-[272px]"
                        on:change={handleFilterSelect}
                        showClearButton
                        disable={isGettingReceipts}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.dateFrom}
                        title="Date from"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingReceipts}
                        maxDate={filters.dateTo ?? undefined}
                        showScrollYearOptions
                    />
                    <SearchInput
                        bind:value={filters.dateTo}
                        title="Date to"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingReceipts}
                        minDate={filters.dateFrom ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <ResetFilterLink
                    bind:filter={filters}
                    defaultFilter={{ paidBy: "" }}
                    onResetFilter={clearFilterDoctor}
                />
            </div>

            {#if isUserAdmin}
                <div class="flex flex-col flex-wrap gap-4 items-end">
                    <Button clickFunc={openPostCSVModal}>
                        Upload EFT Payments CSV
                    </Button>

                    <Button clickFunc={openRecordPaymentModal}>
                        Record Payment
                    </Button>
                </div>
            {/if}
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingReceipts}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                bind:this={scrollTableContainerRef}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnReceiptsArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnReceiptsArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnReceiptsArr,
            fileName: "Pending Refund",
            allDataApi: async () => {
                let res = await apiMethods.getMethod.getReceipts(
                    0,
                    999999,
                    _getSearchParams(),
                );

                const results = await _handleReceipts(res, true);

                return { results };
            },
        }}
    />

    <PostCsvFileModal {isPostCSVModalOpen} closeModal={closePostCSVModal} />
</div>

<RecordPaymentModal {isRecordPaymentModalOpen} {closeRecordPaymentModal} />

{#if isEditPaymentModalOpen}
    <RecordPaymentModal
        isRecordPaymentModalOpen={isEditPaymentModalOpen}
        closeRecordPaymentModal={closeEditPaymentModal}
        paymentDetails={selectedPaymentDetails}
        route="receiptTable"
    />
{/if}
